import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/dateUtils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const PALETTE = ['#4f8ef7', '#7c3aed', '#22c55e', '#f97316', '#eab308', '#06b6d4', '#ec4899', '#ef4444', '#14b8a6', '#a855f7'];
const MAINT_CATEGORIES = ['Repairs', 'Parts', 'Tyres', 'Service'];
const ACTIVE_STATUSES = ['active', 'inactive', 'classic', 'daneira', 'immobilized', 'dokimi_test', 'service', 'restoration'];

const tooltipStyle = {
  background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: 8, color: 'var(--text)', fontSize: 13,
};

const SummaryCard = ({ label, value, sub, color }) => (
  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
    <div style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: color || 'var(--text)', lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>{sub}</div>}
  </div>
);

const ChartCard = ({ title, subtitle, data, dataKey, valueFormatter, children }) => (
  <div className="card" style={{ marginTop: 16 }}>
    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>{subtitle}</div>}
    {data.length === 0 ? (
      <div style={{ fontSize: 13, color: 'var(--text3)', padding: '20px 0' }}>Not enough data yet.</div>
    ) : (
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 40)}>
        <BarChart layout="vertical" data={data} margin={{ top: 4, right: 28, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
          <XAxis type="number" tick={{ fill: 'var(--text3)', fontSize: 11 }} tickFormatter={valueFormatter} />
          <YAxis type="category" dataKey="name" width={92} tick={{ fill: 'var(--text2)', fontSize: 11 }} />
          <Tooltip
            formatter={(val) => [valueFormatter ? valueFormatter(val) : val, '']}
            labelFormatter={(l) => l}
            contentStyle={tooltipStyle}
            cursor={{ fill: 'var(--surface2)' }}
          />
          <Bar dataKey={dataKey} radius={[0, 4, 4, 0]}>
            {data.map((r, i) => <Cell key={r.id} fill={PALETTE[i % PALETTE.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )}
    {children}
  </div>
);

export const FleetInsights = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [veh, exp, mech, svc] = await Promise.all([
        supabase.from('vehicles').select('id, registration_number, brand, model, mileage, status').in('status', ACTIVE_STATUSES),
        supabase.from('expenses').select('vehicle_id, category, amount'),
        supabase.from('mechanical_reports').select('vehicle_id, result, cost'),
        supabase.from('service_records').select('vehicle_id, cost'),
      ]);

      const vehicles = veh.data || [];
      const expenses = exp.data || [];
      const mechanical = mech.data || [];
      const services = svc.data || [];

      const data = vehicles.map((v) => {
        const vExp = expenses.filter((e) => e.vehicle_id === v.id);
        const vMech = mechanical.filter((m) => m.vehicle_id === v.id);
        const vSvc = services.filter((s) => s.vehicle_id === v.id);

        const expTotal = vExp.reduce((s, e) => s + Number(e.amount || 0), 0);
        const mechCost = vMech.reduce((s, m) => s + Number(m.cost || 0), 0);
        const svcCost = vSvc.reduce((s, x) => s + Number(x.cost || 0), 0);

        const maintExp = vExp
          .filter((e) => MAINT_CATEGORIES.includes(e.category))
          .reduce((s, e) => s + Number(e.amount || 0), 0);

        const maintenanceCost = maintExp + mechCost + svcCost;
        const totalCost = expTotal + mechCost + svcCost;

        const repairEvents =
          vMech.filter((m) => m.result === 'Failed' || m.result === 'Needs Repair').length +
          vExp.filter((e) => e.category === 'Repairs').length;

        const mileage = Number(v.mileage || 0);
        const costPerKm = mileage > 0 ? maintenanceCost / mileage : null;

        return {
          id: v.id,
          name: v.registration_number || `${v.brand || ''} ${v.model || ''}`.trim() || '—',
          label: `${v.brand || ''} ${v.model || ''}`.trim(),
          totalCost, maintenanceCost, repairEvents, mileage, costPerKm,
        };
      });

      setRows(data);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return <div className="card"><div style={{ padding: 24, color: 'var(--text3)' }}>Loading fleet insights…</div></div>;
  }
  if (rows.length === 0) {
    return <div className="card"><div style={{ padding: 24, color: 'var(--text3)' }}>No vehicles to analyze yet. Add vehicles and log some costs to see insights.</div></div>;
  }

  const fleetMaint = rows.reduce((s, r) => s + r.maintenanceCost, 0);
  const withMaint = rows.filter((r) => r.maintenanceCost > 0);
  const mostExpensive = [...withMaint].sort((a, b) => b.maintenanceCost - a.maintenanceCost)[0];
  const leastExpensive = [...withMaint].sort((a, b) => a.maintenanceCost - b.maintenanceCost)[0];
  const mostBreakdowns = [...rows].sort((a, b) => b.repairEvents - a.repairEvents)[0];

  const byMaint = [...rows].sort((a, b) => b.maintenanceCost - a.maintenanceCost);
  const byRepairs = rows.filter((r) => r.repairEvents > 0).sort((a, b) => b.repairEvents - a.repairEvents);
  const byCostPerKm = rows.filter((r) => r.costPerKm != null).sort((a, b) => b.costPerKm - a.costPerKm);
  const tableRows = [...rows].sort((a, b) => b.maintenanceCost - a.maintenanceCost);

  const perKmFmt = (v) => `${formatCurrency(v)}/km`;

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 4 }}>
        <SummaryCard label="Fleet maintenance spend" value={formatCurrency(fleetMaint)} sub="Across all active vehicles" />
        <SummaryCard label="Most expensive to maintain" color="#ef4444"
          value={mostExpensive ? mostExpensive.name : '—'}
          sub={mostExpensive ? formatCurrency(mostExpensive.maintenanceCost) : 'No data'} />
        <SummaryCard label="Cheapest to maintain" color="#22c55e"
          value={leastExpensive ? leastExpensive.name : '—'}
          sub={leastExpensive ? formatCurrency(leastExpensive.maintenanceCost) : 'No data'} />
        <SummaryCard label="Most breakdowns" color="#f97316"
          value={mostBreakdowns && mostBreakdowns.repairEvents > 0 ? mostBreakdowns.name : '—'}
          sub={mostBreakdowns && mostBreakdowns.repairEvents > 0 ? `${mostBreakdowns.repairEvents} repair/fault event${mostBreakdowns.repairEvents !== 1 ? 's' : ''}` : 'No faults logged'} />
      </div>

      <ChartCard
        title="Maintenance cost by vehicle"
        subtitle="Repairs, parts, tyres, servicing and mechanical work"
        data={byMaint} dataKey="maintenanceCost" valueFormatter={formatCurrency}
      />

      <ChartCard
        title="Reliability — repair & fault events"
        subtitle="Failed / 'needs repair' mechanical reports plus logged repairs. Higher = breaks down more."
        data={byRepairs} dataKey="repairEvents"
      />

      <ChartCard
        title="Maintenance cost per km"
        subtitle="Maintenance spend ÷ mileage — the fair way to compare cars driven different amounts"
        data={byCostPerKm} dataKey="costPerKm" valueFormatter={perKmFmt}
      />

      {/* Ranking table */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Full ranking</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Vehicle</th>
                <th style={{ textAlign: 'right' }}>Total spend</th>
                <th style={{ textAlign: 'right' }}>Maintenance</th>
                <th style={{ textAlign: 'right' }}>Repairs / faults</th>
                <th style={{ textAlign: 'right' }}>Mileage</th>
                <th style={{ textAlign: 'right' }}>Cost / km</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    {r.label && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{r.label}</div>}
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(r.totalCost)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(r.maintenanceCost)}</td>
                  <td style={{ textAlign: 'right', color: r.repairEvents > 0 ? '#f97316' : 'var(--text3)', fontWeight: r.repairEvents > 0 ? 600 : 400 }}>{r.repairEvents}</td>
                  <td style={{ textAlign: 'right' }}>{r.mileage ? `${r.mileage.toLocaleString()} km` : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{r.costPerKm != null ? perKmFmt(r.costPerKm) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 10 }}>
          Maintenance = repairs, parts, tyres, servicing and mechanical work. Repairs/faults counts failed or
          "needs repair" mechanical reports plus logged repair expenses. Cost/km needs mileage recorded on the vehicle.
        </div>
      </div>
    </div>
  );
};